/**
 * The vocabulary of {{merge_variables}} a message template may use, and a save-time check
 * that an author has not typed one we cannot fill.
 *
 * WHY THIS EXISTS
 *   Because teamio-utility's replaceTemplateVariables only substitutes TRUTHY values
 *   (`if (availableVariables[word])`), a token no sender fills is not replaced — it is sent to
 *   the patient verbatim, braces and all.
 *
 * REVIEWS VARIABLES — CORRECTED 2026-09-26
 *   The case that surfaced this (prod, 2026-08-24) was Mint of Emerson's "Review Request SMS":
 *       "Hi {{first_name}}, thanks for visiting {{practice_name}}! ...  {{review_link}}"
 *   It was read as tokens pasted in from another vendor. It is not: it is the Reviews feature's
 *   OWN seeded template, and reviews-service reviews-worker.js fills exactly {first_name, name,
 *   review_link, practice_name}, which utility.js merges with the standard variables. Treating
 *   them as foreign everywhere broke the Reviews template editor for a real office (2026-09-16:
 *   the untouched default text would not save). They are still unfillable on EVERY other send
 *   path, so they are accepted only on pages tagged `review-request` — the only pages Reviews →
 *   Settings offers — and utils/wikiTags withoutReviewRequestTemplates keeps those pages out of
 *   every general template picker (pinned by cypress/node-tests/template-variable-validation).
 *
 *   Nothing stopped the save. The ONLY component that parsed {{tokens}} at save time was the
 *   length estimator (utils/smsSegments substituteTags), and it is deliberately permissive:
 *   an unrecognised tag gets DEFAULT_SAMPLE ('Xxxxxxxxxxxx') so the character estimate does not
 *   explode. Correct for measuring, but it meant the one place that already enumerated
 *   variables threw the signal away instead of raising it.
 *
 * SCOPE — SMS *AND* EMAIL TEMPLATES
 *   The Wiki is ONE authoring surface: the same + Insert palette, the same variables, and a
 *   page can be flagged as both an SMS and an email template at once (2 live prod pages are).
 *   So the vocabulary is shared, and so is the failure.
 *
 *   Email renders through the SAME engine, not SendGrid's:
 *       utility.js:5128  msgData.emailHtml = await replaceTemplateVariables(
 *                            msgData.emailHtml, msgData.DynamicTemplateData);
 *   DynamicTemplateData (utility.js:4493-4512) carries the same keys as the SMS map, plus
 *   site_logo_url, plus a wholesale spread of msgData.availableVariables. Same truthy gate,
 *   same silent failure.
 *
 *   The pasted review-request pack proves it: "Review Request SMS" AND "Review Request Email"
 *   both carry {{first_name}} / {{practice_name}} / {{review_link}}. Gating only SMS would have
 *   left the email half broken and unguarded.
 *
 *   TRIPLE braces stay excluded. 127 prod pages carry a SendgridTemplateID, and 8 email
 *   templates legitimately use {{{confirm_button}}} — Handlebars' unescaped-HTML form, rendered
 *   by SendGrid rather than by us. Flagging those would be an artefact of our own parsing.
 *
 * EVIDENCE FOR THE LIST BELOW
 *   Measured against every prod template on 2026-08-24: 254 template pages + 66 translations,
 *   then re-checked across all 65 email templates specifically. Every static token in real use
 *   — SMS and email alike — is present here except the three Reviews tokens above. Re-measured
 *   2026-09-26: those appear only on the 6 review-request pages fleet-wide, none of which any
 *   workflow references.
 */

/**
 * Variables the SMS pipeline can actually produce.
 *
 * Sources, and they must stay in step:
 *   - teamio-utility/utility.js `_sendSMS`            (Patient Profile / follow-up path)
 *   - conversations-service/index.js availableVariables (Conversations path)
 *   - teamio-lambda-layer common.buildEmployeeNameVariables (the employee_* family)
 *   - reminder / payment paths, which supply the remainder
 */
export const KNOWN_TEMPLATE_VARIABLES = Object.freeze([
  // patient
  'patient_first_name',
  'patient_last_name',
  'patient_name',
  'patient_language',
  'minor_names',
  // guarantor
  'guarantor_first_name',
  'guarantor_last_name',
  // employee — one family, produced together by the shared layer helper.
  // 'employee_name' is a PERMANENT legacy alias: hidden from the palettes but still valid to
  // type, and still present in 6 live prod templates. Removing it here would block their saves.
  'employee_name',
  'employee_full_name',
  'employee_first_name',
  // site
  'site_id',
  'site_name',
  'site_address1',
  'site_address2',
  'site_city',
  'site_state',
  'site_zip',
  'site_officephone',
  'site_regioncode',
  // email-only: built by utility.js buildSiteLogoUrl into DynamicTemplateData. Unused in any
  // live template today, but producible — so it must not be a save-blocker if someone types it.
  'site_logo_url',
  // email-only HTML block; resolved into dynamic_template_data by utility.js; wiki-service
  // bake normalizes it to triple-brace for SendGrid; stripped from SMS.
  'email_signature_block',
  // scheduling / recall
  'apt_date',
  // weekday of the appointment ("Wednesday"; "miércoles" for a Spanish patient) — filled by teamio-utility next to apt_date
  'apt_dayofweek',
  'apt_provider',
  'online_apttype',
  'recall_due_date',
  'family_appts',
  // money
  'payment_link',
  'patient_balance',
  'total_amount',
  // email-only (Handlebars); listed so an email warning does not flag them
  'confirm_button',
  // plumbing that processLink consumes
  'pat_num',
]);

const KNOWN = new Set(KNOWN_TEMPLATE_VARIABLES);

// The online-scheduling link token's name (reminderMessageDefaults.js BOOKING_LINK_VARIABLE — this file stays import-free,
// a node-test pins that the two agree). Not a known variable; see findUnknownVariables `allowBookingLink`.
const BOOKING_LINK_VARIABLE = 'online_scheduling_link';

// The Forms Settings form-link token's name (utils/formsSettings/formLink.js FORM_LINK_TOKEN — this file stays import-free, a
// node-test pins that the two agree). Not a known variable; see findUnknownVariables `allowFormLink`.
const FORM_LINK_VARIABLE = 'form_link';

/** The tag Reviews → Settings uses to list its SMS/email templates. */
export const REVIEW_REQUEST_TAG = 'review-request';

/**
 * Variables only the Reviews send path fills (reviews-worker.js availableVariables). The worker
 * also sends `name` ("LName, FName"); no prod template uses it and it is a poor greeting, so it is
 * deliberately left out.
 */
export const REVIEW_TEMPLATE_VARIABLES = Object.freeze([
  'review_link',
  'first_name',
  'practice_name',
]);

const REVIEW_ONLY = new Set(REVIEW_TEMPLATE_VARIABLES);

/**
 * Length-estimate samples for the Reviews variables, passed as getSmsGuidance `vars` on
 * review-request pages. The real link is APP_FRONTEND_URL/rv/<shortId>; smsSegments' 12-char
 * default sample would under-count it by ~25 chars. (Kept here, not in smsSegments SAMPLE_VARS,
 * which must stay byte-identical to the layer copy.)
 */
export const REVIEW_SAMPLE_VARS = Object.freeze({
  review_link: 'https://prod.teamio.app/rv/XXXXXXXXXX',
  first_name: 'Christopher',
  practice_name: 'Allay Dental Group',
});

/**
 * Does this tag list carry review-request? Accepts tag names or the editor's {Tag, TagID}
 * objects; exact per-tag match, case/whitespace tolerant. A bare string is NOT a tag list —
 * getWikiPages' comma-joined Tags goes through utils/wikiTags instead.
 */
export function isReviewRequestTagged(tags) {
  if (!Array.isArray(tags)) return false;
  return tags.some((t) => {
    const name = t && typeof t === 'object' ? t.Tag : t;
    return String(name == null ? '' : name).trim().toLowerCase() === REVIEW_REQUEST_TAG;
  });
}

/**
 * Per-form / per-packet link tokens are minted with a UUID and resolved by processLink, so they
 * cannot be enumerated. 47 of them are in live prod templates — treating them as unknown would
 * block a third of the fleet's templates.
 */
const DYNAMIC_VARIABLE = /^patient_link_for_(?:form|packet)_[0-9a-fA-F-]{36}$/;

/**
 * @param {string} name
 * @param {{tags?: Array<string|{Tag:string}>}} [opts]  the page's tags; the Reviews variables
 *        are known only when they include review-request. No opts = the global vocabulary.
 */
export function isKnownTemplateVariable(name, { tags } = {}) {
  const n = String(name == null ? '' : name);
  if (KNOWN.has(n) || DYNAMIC_VARIABLE.test(n)) return true;
  return REVIEW_ONLY.has(n) && isReviewRequestTagged(tags);
}

/** Levenshtein, small inputs only — powers the did-you-mean hint. */
function editDistance(a, b) {
  const m = a.length;
  const n = b.length;
  let prev = Array.from({ length: n + 1 }, (_, j) => j);
  for (let i = 1; i <= m; i += 1) {
    const cur = [i];
    for (let j = 1; j <= n; j += 1) {
      cur[j] = Math.min(
        prev[j] + 1,
        cur[j - 1] + 1,
        prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
      );
    }
    prev = cur;
  }
  return prev[n];
}

/**
 * Closest known variable, or null when nothing is close enough to be a helpful guess.
 * Substring containment wins first: "first_name" -> "patient_first_name" is obvious to a human
 * but 8 edits away, which no distance threshold would ever surface.
 */
export function suggestVariable(name) {
  const n = String(name == null ? '' : name).toLowerCase().trim();
  if (!n) return null;

  const contains = KNOWN_TEMPLATE_VARIABLES
    .filter((k) => k.includes(n) || n.includes(k))
    .sort((a, b) => Math.abs(a.length - n.length) - Math.abs(b.length - n.length));
  if (contains.length) return contains[0];

  let best = null;
  let bestD = Infinity;
  KNOWN_TEMPLATE_VARIABLES.forEach((k) => {
    const d = editDistance(n, k);
    if (d < bestD) { bestD = d; best = k; }
  });
  // Allow roughly a third of the token to differ before we stop guessing.
  return bestD <= Math.max(2, Math.floor(n.length / 3)) ? best : null;
}

/**
 * Every {{token}} in `text`, in document order, de-duplicated.
 *
 * TRIPLE braces are skipped. {{{confirm_button}}} is Handlebars' unescaped-HTML form for
 * SendGrid; our own resolver's /{{(.+?)}}/g would capture "{confirm_button" out of it, so
 * reporting that as an unknown variable would be an artefact of our own parsing, not a real
 * authoring mistake.
 */
export function extractTemplateVariables(text) {
  const src = String(text == null ? '' : text).replace(/\{\{\{[^}]*\}\}\}/g, ' ');
  const out = [];
  const seen = new Set();
  for (const m of src.matchAll(/\{\{([^{}]*)\}\}/g)) {
    const raw = m[1];
    if (seen.has(raw)) continue;
    seen.add(raw);
    out.push(raw);
  }
  return out;
}

/**
 * Unknown variables in `text`, each with a reason and a suggestion where one is obvious.
 *
 * `reason` distinguishes the two ways a token fails, because the fixes differ:
 *   'whitespace' — "{{ patient_first_name }}". The backend looks up availableVariables[word]
 *                  with the captured text VERBATIM, so the padded key misses and the braces
 *                  reach the patient. Looks correct to the author; the fix is to close the gaps.
 *   'unknown'    — no such variable, e.g. a token pasted from another vendor.
 *   'review-only' — a Reviews variable on a page NOT tagged review-request: fillable only by
 *                  the Reviews send path, so anywhere else the braces reach the patient.
 *
 * `allowBookingLink` (default false) is for the message boxes on Appointment Reminders → Settings that may hold the
 * online-scheduling link: the recall slots and the recall-type rules (reminderMessageDefaults slotAllowsBookingLink).
 * There — and only there — `{{online_scheduling_link}}` (spaces inside the braces tolerated, as the send-time swap
 * tolerates them) is not reported. It is deliberately NOT in KNOWN_TEMPLATE_VARIABLES: that list is shared with the
 * wiki editor and the patient send panels, which cannot resolve the token. Everywhere else it stays "unknown".
 *
 * `allowFormLink` (default false) is the same idea for the Forms Settings message boxes (New Patients, Medical History
 * Update): a MessageTemplateBox that has a `link` prop with token 'form_link' passes it, and ONLY there is `{{form_link}}`
 * (spaces inside the braces tolerated) not reported. Not in KNOWN_TEMPLATE_VARIABLES for the same reason as the booking link.
 *
 * @param {string} text
 * @param {{tags?: Array<string|{Tag:string}>, allowBookingLink?: boolean, allowFormLink?: boolean}} [opts]  the page's current tags
 * @returns {Array<{name:string, reason:'unknown'|'whitespace'|'review-only', suggestion:string|null}>}
 */
export function findUnknownVariables(text, { tags, allowBookingLink, allowFormLink } = {}) {
  return extractTemplateVariables(text)
    .map((raw) => {
      const trimmed = raw.trim();
      if (allowBookingLink === true && trimmed === BOOKING_LINK_VARIABLE) return null;
      if (allowFormLink === true && trimmed === FORM_LINK_VARIABLE) return null;
      if (trimmed !== raw && isKnownTemplateVariable(trimmed, { tags })) {
        return { name: raw, reason: 'whitespace', suggestion: trimmed };
      }
      if (isKnownTemplateVariable(raw, { tags })) return null;
      if (REVIEW_ONLY.has(trimmed)) {
        return { name: raw, reason: 'review-only', suggestion: suggestVariable(trimmed) };
      }
      return { name: raw, reason: 'unknown', suggestion: suggestVariable(trimmed) };
    })
    .filter(Boolean);
}

/**
 * Author-facing sentence for a save-blocking toast. Names every offender, because a message
 * that says only "invalid variable" leaves the author hunting through their own template.
 */
export function describeUnknownVariables(unknown) {
  if (!unknown || !unknown.length) return '';
  const parts = unknown.map((u) => {
    if (u.reason === 'whitespace') {
      return `{{${u.name}}} has extra spaces inside the braces — use {{${u.suggestion}}}`;
    }
    if (u.reason === 'review-only') {
      const alt = u.suggestion ? `, use {{${u.suggestion}}}` : '';
      return `{{${u.name}}} only works in Reviews templates (pages tagged ${REVIEW_REQUEST_TAG}) — ` +
        `add that tag${alt}, or remove it`;
    }
    return u.suggestion
      ? `{{${u.name}}} is not a Teamio variable — did you mean {{${u.suggestion}}}?`
      : `{{${u.name}}} is not a Teamio variable`;
  });
  const lead = unknown.length === 1
    ? 'This template uses a variable we cannot fill in: '
    : 'This template uses variables we cannot fill in: ';
  return `${lead}${parts.join('; ')}. Patients would receive the text exactly as written, ` +
    'braces and all. Pick a variable from the + Insert menu, or delete it.';
}

export default {
  KNOWN_TEMPLATE_VARIABLES,
  REVIEW_REQUEST_TAG,
  REVIEW_TEMPLATE_VARIABLES,
  REVIEW_SAMPLE_VARS,
  isReviewRequestTagged,
  isKnownTemplateVariable,
  suggestVariable,
  extractTemplateVariables,
  findUnknownVariables,
  describeUnknownVariables,
};
